class BikeSizer extends HTMLElement {
  constructor() {
    super();
    this.scale = this.querySelector(".bike-sizer-scale");
    this.heightValue = this.querySelector(".bike-sizer-selected-height-value");
    this.variantName = this.querySelector(".bike-sizer-selected-variant-name");
    this.sizes = this.getSizesFromData();

    // Translations
    this.feetLabel = this.dataset.feetLabel || "ft";
    this.inchesLabel = this.dataset.inchesLabel || "in";
    this.scaleLabel = this.dataset.scaleLabel || "Height scale";

    this.addEventListeners();
    this.updateHeight();
    this.updateVariant();
    this.updateAccentColor();
    this.updateThumbSize();
    this.updateThumbSVG();
    this.updateSliderProgress();
    this.updateAriaAttributes();
    window.addEventListener("resize", () => this.handleResize());
  }

  //   Grab the sizes from the data attributes on bike-sizer
  getSizesFromData() {
    const sizeOrder = ["xs", "s", "m", "l", "xl"];
    let sizes = [];
    sizeOrder.forEach((size) => {
      const val = this.dataset[size];
      if (val) {
        sizes.push({
          name: size.toUpperCase(),
          //   convert the value to inches, e.g 5.08 to 5'8"
          max: this.decimalToInches(parseFloat(val)),
        });
      }
    });
    return sizes;
  }

  // Listen for scale change to update all elements
  addEventListeners() {
    if (this.scale) {
      this.scale.addEventListener("input", () => {
        this.updateHeight();
        this.updateVariant();
        this.updateAccentColor();
        this.updateThumbSize();
        this.updateSliderProgress();
        this.updateAriaAttributes();
        requestAnimationFrame(() => this.updateThumbSVG());
      });
    }
  }

  //   Handle things that will need updated styles on resize
  handleResize() {
    this.updateThumbSize();
    this.updateSliderProgress();
    requestAnimationFrame(() => this.updateThumbSVG()); //Delay it until the thumb size is updated first
  }

  //  Conversion of metafield values (5.03) to inches (63) to feet (5'3")
  decimalToInches(decimal) {
    const feet = Math.floor(decimal);
    const inches = Math.round((decimal - feet) * 100);
    return feet * 12 + inches;
  }
  inchesToFeetInches(totalInches) {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}'${inches}"`;
  }

  // Update the text of "Your height" element
  updateHeight() {
    // If its already correct, return
    if (!this.scale || !this.heightValue) return;
    const val = parseInt(this.scale.value, 10); //(inches)
    this.heightValue.textContent = this.inchesToFeetInches(val);

    // Update centimeters
    const cmValue = Math.round(val * 2.54);
    const cmElem = this.querySelector(".bike-sizer-selected-height-value--cm");
    if (cmElem) {
      cmElem.textContent = `${cmValue} cm`;
    }
  }

  //   Update the text variant name for the recommended size
  updateVariant() {
    if (!this.scale || !this.variantName) return;
    const val = parseFloat(this.scale.value);

    // Get scale type from data attribute
    const scaleType = this.dataset.scale;

    // Define the mapping for word and number scales
    const wordLabels = ["Short", "Long"];
    const metafieldOrder = ["xxs", "xs", "s", "m", "l", "xl", "xxl"];

    // Build an array of { name, max } for the current scale
    let sizes = [];
    if (scaleType === "word") {
      // Only use xxs and xs heights
      sizes = [
        {
          name: wordLabels[0],
          max: this.decimalToInches(parseFloat(this.dataset.xxs)),
        },
        {
          name: wordLabels[1],
          max: this.decimalToInches(parseFloat(this.dataset.xs)),
        },
      ];
    } else if (scaleType === "number") {
      const uniqueNumberLabels = ["47", "50", "52", "54", "56", "58", "60"];
      sizes = uniqueNumberLabels
        .map((label, i) => {
          // Accept both "47" and "47cm" as valid
          const possibleLabels = [label, `${label}cm`];
          const key = metafieldOrder[i];
          const rawValue = this.dataset[key];
          const valInches = rawValue
            ? this.decimalToInches(parseFloat(rawValue))
            : null;
          const foundLabel = possibleLabels.find((l) =>
            this.dataset.size_values?.includes(l)
          );
          const result = {
            name: foundLabel || `${label}cm`,
            max: valInches,
          };
          return result;
        })
        .filter((s) => s.max !== null);
    } else {
      // Default: use the sizes array as before (letter scale)
      sizes = this.sizes;
    }

    // Find the recommended size
    let recommended = sizes[0];
    let recommendedIndex = 0;
    for (let i = 0; i < sizes.length; i++) {
      if (val <= sizes[i].max) {
        recommended = sizes[i];
        recommendedIndex = i;
        break;
      }
    }

    // Show split label at size boundaries (e.g. "S/M") when the selected height
    // is exactly at the top of a size range and a next size exists.
    // This indicates the rider is on the cusp between two sizes.
    if (val === recommended.max && recommendedIndex + 1 < sizes.length) {
      this.variantName.textContent = `${recommended.name}/${sizes[recommendedIndex + 1].name}`;
    } else {
      this.variantName.textContent = recommended.name;
    }
  }

  // Math stuff to work out the color gradient
  interpolateColorRGB(rgb1, rgb2, factor) {
    return rgb1.map((v, i) => Math.round(v + (rgb2[i] - v) * factor));
  }

  // Convert RGB to hex
  rgbToHex([r, g, b]) {
    return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
  }

  // updates the --sizer-accent CSS variable on the section based on the slider value
  updateAccentColor() {
    if (!this.scale) return;
    const val = parseInt(this.scale.value, 10);
    const scaleType = this.dataset.scale;

    // Rough guidance for colors (e.g xs should be pink, m should be yellow, l should be blue)
    const colorXS = [255, 65, 180]; // pink
    const colorM = [254, 207, 0]; // yellow
    const colorL = [0, 162, 225]; // blue
    let color = colorXS;

    // Build sizes array for the current scale type
    let sizes = [];
    if (scaleType === "word") {
      const shortMax = this.decimalToInches(parseFloat(this.dataset.xxs));
      const longMax = this.decimalToInches(parseFloat(this.dataset.xs));
      if (val <= shortMax) {
        color = colorXS;
      } else if (val > shortMax && val <= longMax) {
        // Interpolate between pink and yellow
        const factor = (val - shortMax) / (longMax - shortMax);
        color = this.interpolateColorRGB(colorXS, colorM, factor);
      } else if (val > longMax) {
        color = colorM;
      }
    } else if (scaleType === "number") {
      const numberLabels = ["47", "50", "52", "54", "56", "58", "60"];
      const metafieldOrder = ["xxs", "xs", "s", "m", "l", "xl", "xxl"];
      sizes = numberLabels
        .map((label, i) => {
          const key = metafieldOrder[i];
          const rawValue = this.dataset[key];
          const valInches = rawValue
            ? this.decimalToInches(parseFloat(rawValue))
            : null;
          return {
            name: label + "cm",
            max: valInches,
          };
        })
        .filter((s) => s.max !== null);

      // Number scale: index-based (all sizes are always sequential)
      const sMax = sizes[1]?.max || 0;
      const mMax = sizes[2]?.max || 0;
      const lMax = sizes[3]?.max || 0;
      const xlMax = sizes[4]?.max || 0;

      if (val <= sMax && sMax > 0) {
        color = colorXS;
      } else if (val > sMax && val <= mMax && mMax > 0) {
        const factor = (val - sMax) / (mMax - sMax);
        color = this.interpolateColorRGB(colorXS, colorM, factor);
      } else if (val > mMax && val <= lMax && lMax > 0) {
        const factor = (val - mMax) / (lMax - mMax);
        color = this.interpolateColorRGB(colorM, colorL, factor);
      } else if (val > lMax) {
        color = colorL;
      }
    } else {
      // Letter scale: use name-based lookup so missing sizes (e.g. no XS) don't shift indices
      sizes = this.sizes;
      const findMax = (name) =>
        sizes.find((s) => s.name === name)?.max || 0;
      const sMax = findMax("S");
      const mMax = findMax("M");
      const lMax = findMax("L");

      if (val <= sMax && sMax > 0) {
        color = colorXS; // S and below = pink
      } else if (val > sMax && val <= mMax && mMax > 0) {
        const factor = (val - sMax) / (mMax - sMax);
        color = this.interpolateColorRGB(colorXS, colorM, factor); // pink → yellow
      } else if (val > mMax && val <= lMax && lMax > 0) {
        const factor = (val - mMax) / (lMax - mMax);
        color = this.interpolateColorRGB(colorM, colorL, factor); // yellow → cyan
      } else if (val > lMax) {
        color = colorL; // L and above = cyan
      }
    }

    // Debug output
    // console.log("updateAccentColor:");
    // console.log("  scaleType:", scaleType);
    // console.log("  slider value (inches):", val);
    // console.log(
    //   "  sMax:",
    //   sMax,
    //   "mMax:",
    //   mMax,
    //   "lMax:",
    //   lMax,
    //   "xlMax:",
    //   xlMax,
    //   "xxlMax:",
    //   xxlMax
    // );
    // console.log("  sizes:", sizes);
    // console.log("  chosen color (RGB):", color, "hex:", this.rgbToHex(color));

    this.closest(".bike-sizer-section")?.style.setProperty(
      "--sizer-accent",
      this.rgbToHex(color)
    );
  }
  //   Update the thumb size based on the scale value so it grows the taller the height
  updateThumbSize() {
    if (!this.scale) return;
    const isMobile = window.matchMedia("(max-width:989px)").matches;
    const section = this.closest(".bike-sizer-section");
    if (!section) return;

    if (isMobile) {
      // On mobile, always use full SVG size
      section.style.setProperty("--thumb-width", `32px`);
      section.style.setProperty("--thumb-height", `64.5px`);
      return;
    }

    const min = parseInt(this.scale.min, 10);
    const max = parseInt(this.scale.max, 10);
    const val = parseInt(this.scale.value, 10);

    // Set min/max thumb heights for desktop
    const minThumbHeight = 80;
    const maxThumbHeight = 129;

    // Calculate scale factor (0 at min, 1 at max)
    const factor = (val - min) / (max - min);
    const thumbHeight =
      minThumbHeight + (maxThumbHeight - minThumbHeight) * factor;
    // Maintain aspect ratio (64:129)
    const thumbWidth = (thumbHeight * 64) / 129;

    section.style.setProperty("--thumb-width", `${thumbWidth}px`);
    section.style.setProperty("--thumb-height", `${thumbHeight}px`);
  }

  // Update the SVG to the correct size/color
  updateThumbSVG() {
    if (!this.scale) return;
    const section = this.closest(".bike-sizer-section");
    // Get the current accent color
    const accent = getComputedStyle(section)
      .getPropertyValue("--sizer-accent")
      .trim();

    // On mobile, always use full SVG size
    const isMobile = window.matchMedia("(max-width:989px)").matches;
    const width = isMobile
      ? 32
      : parseFloat(section.style.getPropertyValue("--thumb-width")) || 64;
    const height = isMobile
      ? 64.5
      : parseFloat(section.style.getPropertyValue("--thumb-height")) || 129;

    // SVG with updated size and color
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 64 129' fill='none'><g transform='scale(1,-1) translate(0,-129)'><path d='M45.5291 59.8296L64 94.1526H45.5291L64 128.441H0L18.4998 94.1526H0L18.4998 59.8296H0L32.0144 0.441406L64 59.8296H45.5291Z' fill='${accent}'/></g></svg>`;

    // Encode SVG for CSS
    const encoded = `url("data:image/svg+xml,${encodeURIComponent(svg)
      .replace(/'/g, "%27")
      .replace(/"/g, "%22")}")`;
    this.scale.style.setProperty("--thumb-svg", encoded);
  }

  // Update bar to the correct colour background with progress
  updateSliderProgress() {
    if (!this.scale) return;
    const min = parseInt(this.scale.min, 10);
    const max = parseInt(this.scale.max, 10);
    const val = parseInt(this.scale.value, 10);
    // Calculate progress as a percentage (0% at bottom, 100% at top)
    const percent = ((val - min) / (max - min)) * 100;
    // Detect if we're on medium (horizontal)
    const isMedium = window.matchMedia("(max-width: 989px)").matches;
    // For vertical: invert progress, for horizontal: do not invert
    const progress = isMedium ? percent : 100 - percent;
    this.scale.style.setProperty("--progress", `${progress}%`);
  }

  updateAriaAttributes() {
    if (!this.scale) return;
    const min = parseInt(this.scale.min, 10);
    const max = parseInt(this.scale.max, 10);
    const val = parseInt(this.scale.value, 10);

    // Convert inches to feet/inches for aria-valuetext
    const toFeetInches = (inches) => {
      const feet = Math.floor(inches / 12);
      const inch = inches % 12;
      return `${feet} ${this.feetLabel} ${inch} ${this.inchesLabel}`;
    };

    this.scale.setAttribute("aria-valuemin", min);
    this.scale.setAttribute("aria-valuemax", max);
    this.scale.setAttribute("aria-valuenow", val);
    this.scale.setAttribute("aria-valuetext", toFeetInches(val));
    this.scale.setAttribute("aria-label", this.scaleLabel);
  }
}

customElements.define("bike-sizer", BikeSizer);
